import { PlayerImage } from "@/components/PlayerImage";
import { Separator } from "@/components/ui/separator";
import { ArrowRightIcon } from "@roman/shared/components/ArrowRightIcon";
import { CaptainIcon, ThumbaIcon, ViceCaptainIcon } from "@roman/shared/components/Icons";
import { UpGradientIcon } from "@roman/shared/components/UpGradientIcon";
import type { Player } from "@roman/shared/types/Player";
import type { RoundDetails } from "@roman/shared/types/RoundDetails";
import { cn } from "@roman/shared/utils";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface SchedulePlayersTabProps {
  captain: Player;
  viceCaptain?: Player;
  round: RoundDetails;
  selectedPlayers: string[];
  roundEntryUnitId: string;
  entryId: string;
}

export const SchedulePlayersTab = ({
  captain,
  viceCaptain,
  round,
  roundEntryUnitId,
  entryId,
  selectedPlayers,
}: SchedulePlayersTabProps) => {
  const navigate = useNavigate();
  const getPlayerById = useCallback((id: string | null, players: Player[]) => {
    if (!id) return null;
    return players.find((player) => player.id === id) ?? null;
  }, []);

  if (!captain) {
    // Todo: Error handler here
    return <div>Captain can't be null</div>;
  }

  return (
    <div className="self-center w-full mt-6">
      <div className="bg-light-blue py-3 rounded-md">
        <div className="px-2 grid grid-cols-2">
          <div className="w-full flex flex-row gap-2">
            <div className="place-content-center rounded-lg">
              <div className="flex justify-center items-center w-[38px]">
                <PlayerImage name={captain.name} url={captain.imageUrl} />
              </div>
            </div>

            <div className="flex flex-col">
              <div className="flex w-[90px] justify-center px-2 py-1 text-white rounded-full bg-primary items-center">
                <CaptainIcon className="w-[14px]" />
                <span className="pl-1 text-[8px] font-extrabold content-center ">キャプテンシ</span>
              </div>
              <div className="flex">
                <span className="pl-1 pt-1 text-primary text-[12px] font-bold whitespace-nowrap">活躍ポイント2倍</span>
                <div className="content-end pl-1">
                  <UpGradientIcon />
                </div>
              </div>
              <span className={cn("pl-1 text-black font-bold text-[10px] text-[#2D2D2D] opacity-100")}>
                {captain.name}
              </span>
            </div>
          </div>
          {round.type === "Round0002" && (
            <div className="w-full flex flex-row gap-2">
              <div className="place-content-center rounded-lg">
                <div className="flex justify-center items-center w-[38px]">
                  {viceCaptain?.name ? (
                    <PlayerImage name={viceCaptain?.name || ""} url={viceCaptain?.imageUrl || ""} />
                  ) : (
                    <div className="w-[38px] h-[57px] bg-[#1F1C1C99] place-content-center rounded-lg">
                      <div className="flex justify-center items-center">
                        <ThumbaIcon color="darkGray" className="w-5" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <div className="flex w-[90px] items-center justify-center px-2 py-1 text-white rounded-full bg-secondary">
                  <ViceCaptainIcon className="w-10" />
                  <span className="pl-1 text-[8px] whitespace-nowrap font-extrabold content-center">
                    副キャプテンシ
                  </span>
                </div>
                <div className="flex">
                  <span className="pl-1 pt-1 text-secondary text-xs font-bold">活躍ポイント1.5倍</span>
                  <div className="content-end pl-1">
                    <UpGradientIcon />
                  </div>
                </div>
                <span
                  className={cn(
                    "pl-1 text-black font-bold text-[10px] text-[#2D2D2D]",
                    viceCaptain?.name ? "opacity-100" : "opacity-0",
                  )}
                >
                  {viceCaptain?.name || ""}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        {/* // Player cards */}
        <div className="my-3">
          {selectedPlayers.map((playerId, index) => {
            const player = getPlayerById(playerId, [...round.players.homePlayers, ...round.players.awayPlayers]);
            const isCaptain = captain.id === playerId;
            const isViceCaptain = viceCaptain?.id === playerId;
            const captainSelectBackground = isCaptain ? "bg-primary" : "bg-disabled";
            const viceCaptainSelectBackground = isViceCaptain ? "bg-secondary" : "bg-disabled";
            if (!player) return null;

            return (
              <div className="flex flex-col divide-y" key={player.id}>
                <div key={playerId} className="grid grid-cols-8 justify-center gap-3 py-2 items-center">
                  <div className="col-span-3 w-full flex flex-row gap-3">
                    <div className="max-w-14 place-content-center rounded-lg">
                      <PlayerImage
                        url={player.imageUrl}
                        name={player.name}
                        isCaptain={isCaptain}
                        isViceCaptain={isViceCaptain}
                        className="w-[50px]"
                      />
                    </div>
                    <div className="flex flex-col whitespace-nowrap">
                      <div>
                        <span className="font-bold text-sm">{player.name}</span>
                      </div>
                      <div className="flex">
                        <span className="content-center text-[10px] font-bold">活躍ポイント :</span>
                        <span className="pl-[2px] text-[12px] font-bold">
                          {player.averageFantasyPoint.toFixed(2)} pts
                        </span>
                      </div>
                      <span className="text-[10px]">(直近8試合の平均)</span>
                      <div className="flex cursor-pointer text-[10px] text-xs font-medium text-primary pt-1">
                        <div
                          className="cursor-pointer flex"
                          onClick={() =>
                            navigate(`/scheduled/${roundEntryUnitId}/entries/${entryId}?playerId=${player.id}`)
                          }
                        >
                          <span className="content-center">選択履歴 </span>
                          <div className="content-center ml-1">
                            <ArrowRightIcon />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 col-span-5 justify-end self-center">
                    <div
                      className={cn(
                        "py-[10px] border border-white whitespace-nowrap text-white rounded-full cursor-pointer min-w-[86px] text-center font-bold text-xs",
                        captainSelectBackground,
                      )}
                    >
                      キャプテン
                    </div>

                    {round.type === "Round0002" && (
                      <div
                        className={cn(
                          "py-[10px] border border-white whitespace-nowrap text-white rounded-full cursor-pointer min-w-[86px] text-center font-bold text-xs",
                          viceCaptainSelectBackground,
                        )}
                      >
                        副キャプテン
                      </div>
                    )}
                  </div>
                </div>
                {index <= selectedPlayers.length - 1 && (
                  <Separator orientation="horizontal" className="bg-white opacity-30 h-[0.5px]" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
